import { FC, useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import { ColType, SummaryGroupBy } from 'common/types/summaryDefinitions';
import { ValueType, ValueTypeCategory } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { CATEGORY_LABELS, CATEGORY_ORDER } from './columnCategories';

const GROUP_BY_EXCLUDED: Record<SummaryGroupBy, string> = {
  company: 'fund_name',
  fund: 'company_name',
};

interface AddColumnsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: ColType[]) => void;
  availableColumns: ValueType[];
  currentColumns: ColType[];
  groupBy?: SummaryGroupBy;
  disabled?: boolean;
}

const AddColumnsModal: FC<AddColumnsModalProps> = ({
  open,
  onClose,
  onSave,
  availableColumns,
  currentColumns,
  groupBy = 'company',
  disabled,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<ColType>>(new Set());

  const searchRef = useRef<HTMLInputElement>(null);

  const handleEnter = () => {
    setSearch('');
    setSelected(new Set());
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (open) handleEnter();
  }, [open]);

  const excluded = GROUP_BY_EXCLUDED[groupBy];
  const currentSet = new Set(currentColumns);

  // Only show columns not already in the view
  const addable = availableColumns.filter(vt => vt.id !== excluded && !currentSet.has(vt.id as ColType));

  const grouped = (() => {
    const term = search.toLowerCase();
    const map = new Map<ValueTypeCategory, ValueType[]>();
    addable
      .filter(vt => vt.displayName.toLowerCase().includes(term))
      .forEach(vt => {
        const cat = vt.metadata?.category ?? 'custom';
        if (!map.has(cat)) {
          map.set(cat, []);
        }
        map.get(cat)!.push(vt);
      });
    return CATEGORY_ORDER
      .map(cat => ({ cat, label: CATEGORY_LABELS[cat], items: map.get(cat) ?? [] }))
      .filter(g => g.items.length > 0);
  })();

  const toggle = (id: ColType) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = () => {
    onSave([...currentColumns, ...selected] as ColType[]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Columns
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search columns..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputRef={searchRef}
          sx={{ mb: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <List dense sx={{ maxHeight: 400, overflow: 'auto', py: 0 }} subheader={<li />}>
          {grouped.map(group => (
            <li key={group.cat}>
              <ul style={{ padding: 0, margin: 0 }}>
                <ListSubheader sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                  lineHeight: '28px',
                  backgroundColor: 'background.paper',
                }}>
                  {group.label}
                </ListSubheader>
                {group.items.map(vt => (
                  <Box
                    key={vt.id}
                    component="li"
                    onClick={() => !disabled && toggle(vt.id as ColType)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 0.25,
                      px: 1.5,
                      cursor: disabled ? 'default' : 'pointer',
                      backgroundColor: selected.has(vt.id as ColType) ? 'action.selected' : 'transparent',
                      '&:hover': { backgroundColor: selected.has(vt.id as ColType) ? 'action.selected' : 'action.hover' },
                    }}>
                    <Checkbox size="small" checked={selected.has(vt.id as ColType)} disableRipple sx={{ p: 0.5, mr: 1 }} />
                    <Typography variant="body2">{vt.displayName}</Typography>
                  </Box>
                ))}
              </ul>
            </li>
          ))}
          {grouped.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {search ? 'No columns match your search.' : 'All available columns are already added.'}
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="caption" color="text.secondary">
          {selected.size} of {addable.length} available selected
        </Typography>
        <div>
          <Button onClick={onClose} disabled={disabled}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={disabled || selected.size === 0}>
            Add Columns{selected.size > 0 ? ` (${selected.size})` : ''}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddColumnsModal;
